import {BehaviorSubject, from, Observable, of, switchMap} from 'rxjs';
import {AbstractService} from './abstract.service';
import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpStatusCode} from '@angular/common/http';
import {translateBusinessError} from '../constants/business-errors';
import {ExceptionResponse, ExceptionResponseError} from '../dto';
import {ApiService} from '../interfaces/api-service.interface';
import {ApiParams} from '../interfaces/api-options.interface';

/**
 * Abstract API object.
 * Extend this class to get the default API configuration.
 */
@Injectable()
export abstract class AbstractApiService extends AbstractService implements ApiService {
  /**
   * Represents an instance of the HttpClient service used for making HTTP requests.
   * The HttpClient instance is injected into a variable to enable communication with backend services.
   * This variable enables sending, retrieving, and manipulating HTTP requests and responses.
   */
  protected http: HttpClient = inject(HttpClient);

  /**
   * A reactive variable that represents the state of an API, implemented as a BehaviorSubject.
   * It can emit and subscribe to string values or null.
   *
   * This variable holds the latest value emitted and provides an initial value of `null`.
   * It is commonly used in reactive programming scenarios where real-time updates
   * or state management of an API endpoint or similar constructs are required.
   *
   * Type: BehaviorSubject<string | null>
   */
  api$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  /**
   * A reactive variable implemented as a BehaviorSubject to manage
   * an ID value. It holds the current ID value or `null` if not set.
   * The BehaviorSubject emits the current value to new subscribers
   * immediately upon subscription.
   *
   * The value of `id$` can either be a number representing the ID
   * or `null` to indicate no ID is set. The reactive nature of
   * BehaviorSubject ensures that changes to the value are immediately
   * propagated to all subscribers.
   */
  id$: BehaviorSubject<number | null> = new BehaviorSubject<number | null>(null);

  /**
   * Retrieves the current `id` value, converting it to a string if it exists or returning null if it is absent.
   *
   * @return {string | null} The `id` as a string if it exists, otherwise null.
   */
  get id(): string | null {
    const id = this.id$.value;
    return id ? String(id) : null;
  }

  /**
   * Getter method for retrieving the endpoint associated with the implementation.
   * Could include a template with placeholders for parameters.
   *
   * @return {string | null} The endpoint as a string if defined; otherwise, null.
   */
  abstract get endpoint(): string | null;

  /**
   * Constructs and returns a URL based on the provided parameters.
   * The method handles multiple cases, including appending a string or number
   * parameter, resolving placeholder segments in the base endpoint with an object,
   * or returning the base URL if no parameters are provided.
   *
   * @param {string|number|Record<string, string|number>} [params] A parameter that can either be:
   *   - A string or number to append to the base URL.
   *   - An object containing key-value pairs to replace placeholder segments (e.g.,id) in the base endpoint.
   *   - Undefined to return the base URL without modification.
   * @return {string} The constructed URL based on the input parameters and the base endpoint.
   */
  url(params?: ApiParams): string {
    // Start with the base endpoint
    const baseUrl = this.endpoint ?? '';
    // Handle simple cases
    if (!params) {
      // If no params and URL has placeholders, remove placeholder segments
      if (baseUrl.includes(':')) {
        const segments = baseUrl.split('/');
        const resultSegments = segments.filter(segment => !segment.startsWith(':'));
        return resultSegments.join('/');
      }
      return baseUrl;
    }
    if (typeof params === 'string' || typeof params === 'number') {
      return `${baseUrl}/${params}`;
    }
    // For object parameters, process the URL segments
    if (baseUrl.includes(':')) {
      const segments = baseUrl.split('/');
      const resultSegments = [];
      // Process each segment
      for (const segment of segments) {
        // If the segment is a placeholder (starts with ':')
        if (segment.startsWith(':')) {
          const paramName = segment.substring(1);
          // Only include if a parameter is provided
          if (params[paramName] !== undefined) {
            resultSegments.push(params[paramName].toString());
          }
        } else {
          // Always include non-placeholder segments
          resultSegments.push(segment);
        }
      }
      return resultSegments.join('/');
    }

    // Fallback for non-template URLs
    return baseUrl;
  }


  /**
   * Handles an HTTP error response by logging the error, normalizing error details, and optionally redirecting to a specified URL.
   * This method constructs an `ExceptionResponse` object from the received error and throws it as an observable error.
   *
   * @param {HttpErrorResponse} response The HTTP error response to handle.
   * @param {Object} [options] Optional parameters for handling the error.
   * @param {string} [options.redirectTo] An optional URL to redirect the user to after processing the error.
   * @return {Observable<never>} An observable that streams the error after processing the response or redirects to the specified URL.
   */
  handleHttpErrorResponse(response: HttpErrorResponse, options?: { redirectTo: string }): Observable<never> {
    this.logger.error(this.constructor.name, 'Http error response', response);
    const errors = this.normalizeErrors(response);
    const hasFieldErrors = errors.some(e => e.property && (e.constraints || (e.children as unknown as unknown[])?.length));
    // Surface the backend message for validation (400), business-rule conflicts
    // (409, e.g. `ASSIGNMENT.MOBILITY_MISMATCH`), confirmation-required (428, whose message is
    // a ready-to-show localized sentence with the impact counts in it) and forbidden business
    // rules (403, e.g. `PROGRAM.EDIT_NOT_OWNER` — "only the creator can edit it"), translating
    // known error keys.
    const showsBackendMessage =
      (response.status === HttpStatusCode.BadRequest
        || response.status === HttpStatusCode.Conflict
        || response.status === HttpStatusCode.PreconditionRequired
        || response.status === HttpStatusCode.Forbidden)
      && !hasFieldErrors
      && typeof response.error?.message === 'string';
    const backendMessage: string | null = showsBackendMessage
      ? translateBusinessError(response.error.message)
      : null;
    const error: ExceptionResponse = {
      status: response.status,
      message: backendMessage ?? $localize`Abbiamo riscontrato un errore imprevisto.`,
      errors,
    };
    const redirectTo: string | null = options?.redirectTo ?? null;
    return of(redirectTo).pipe(
      switchMap((redirectTo: string | null) => {
        if (redirectTo === null) return of(true);
        return from(this.router.navigateByUrl(redirectTo));
      }),
      switchMap(() => { throw error; })
    );
  }

  private normalizeErrors(response: HttpErrorResponse): ExceptionResponseError[] {
    if (response.status === HttpStatusCode.BadRequest && Array.isArray(response.error?.message)) {
      return response.error.message as ExceptionResponseError[];
    }
    if (Array.isArray(response.error?.errors)) {
      return (response.error.errors as Array<ExceptionResponseError & { constraint?: Record<string, string> }>)
        .map(e => ({...e, constraints: e.constraints ?? e.constraint}));
    }
    return [];
  }
}
